import React, { useCallback, useMemo } from 'react';
import styled from 'styled-components';
import media from 'styled-media-query';
import { CSSTransition } from 'react-transition-group';

import StyleConfig from 'src/constants/StyleConfig';
import { roundMaximumQuantity } from 'src/helpers/NotificationHelper';
import { daysAgo } from 'src/helpers/UtilityHelper';

import CheckBox from 'src/components/atoms/form/CheckBox';
import SelectBox from 'src/components/atoms/form/SelectBox';
import InputLabelHolder from 'src/components/molecules/form/InputLabelHolder';
import { ValidateMessageIfError } from 'src/components/atoms/form/ErrorMessage';

type Props = {
  orderBread: {
    selected: boolean;
    shopId: string;
    shopName: string;
    shopAddress: string;
    notificationId: string;
    breadId: string;
    isStopped: boolean;
    name: string;
    description: string;
    image: string;
    price: number;
    maximumOrder: number;
    quantity: number;
    userCount: number;
    lastNotificationTime: undefined | Date;
  };
  errors: string[];
  onSelect: () => void;
  onChangeMaximumOrder: (num: number | null) => void;
  onChangeQuantity: (num: number | null) => void;
};

export const NotificationCreateOrderBread = ({ ...props }: Props) => {
  const {
    orderBread: {
      selected,
      isStopped,
      name,
      image,
      maximumOrder,
      quantity,
      userCount,
      lastNotificationTime,
    },
    errors,
    onSelect,
    onChangeMaximumOrder,
    onChangeQuantity,
  } = props;

  const quantityOptions = useCallback(() => {
    const options = [...Array(50)].map((_, i) => ({
      value: `${i + 1}`,
      content: `${i + 1}個`,
    }));
    options.unshift({ value: '0', content: '----' });
    return options;
  }, []);

  const maximumOrderOptions = useCallback(() => {
    const options = [...Array(quantity)].map((_, i) => ({
      value: `${i + 1}`,
      content: `${i + 1}個まで`,
    }));
    options.unshift({ value: '0', content: '----' });
    return options;
  }, [quantity]);

  const wrapOnSelect = useCallback(() => {
    if (!isStopped) {
      onSelect();
    }
  }, [isStopped, onSelect]);

  // 在庫が最大注文数を下回った場合の処理
  const wrapOnChangeQuantity = useCallback(
    (num: number | null) => {
      const quantity = roundMaximumQuantity(num);
      onChangeQuantity(quantity);
      if (maximumOrder > quantity) {
        onChangeMaximumOrder(quantity);
      }
    },
    [onChangeMaximumOrder, onChangeQuantity, maximumOrder],
  );

  const isDisabledMaximumOrder = useMemo(() => quantity === 0, [quantity]);

  const getDaysAgo = useMemo(
    () => (lastNotificationTime !== undefined ? daysAgo(lastNotificationTime) : '-'),
    [lastNotificationTime],
  );

  return (
    <StyledOrderBread selected={selected} isStopped={isStopped}>
      <div className="content">
        <div className="content-thumbnail">
          <img src={image} alt="" role="presentation" onClick={wrapOnSelect} />
        </div>
        <div className="content-text">
          <h3 className="content-text-name" onClick={wrapOnSelect}>
            {name}
          </h3>
          <p className="content-text-former" onClick={wrapOnSelect}>
            前回のお知らせ：{getDaysAgo}
          </p>
          <p className="content-text-count" onClick={wrapOnSelect}>
            焼き上がり通知登録人数：<span>{userCount}</span>人
          </p>
        </div>
        {!isStopped && (
          <StyledToggleButton selected={selected}>
            {/* <div onClick={wrapOnSelect}>
              {selected ? (
                <GrCheckboxSelected size={28} />
              ) : (
                <GrCheckbox size={28} />
              )}
            </div> */}
            <CheckBox children={''} isChecked={selected} onChange={() => wrapOnSelect()} />
          </StyledToggleButton>
        )}
      </div>
      <CSSTransition in={selected} timeout={100} classNames="forms" unmountOnExit>
        {() => (
          <>
            <div className="input-quantity">
              <InputLabelHolder labelName="販売する個数を選択してください" isRequired />
              <SelectBox
                id="quantity"
                showDefaultOption={false}
                inputValue={quantity}
                onChange={(num) => wrapOnChangeQuantity(parseInt(num))}
                options={quantityOptions()}
              />
            </div>

            <div className="input-maximumQuantity">
              <InputLabelHolder
                labelName="一人のお客様が購入できる最大数を選択してください"
                isRequired
              />
              <SelectBox
                id="maximumOrder"
                showDefaultOption={false}
                inputValue={maximumOrder}
                onChange={(num) => onChangeMaximumOrder(parseInt(num))}
                options={maximumOrderOptions()}
                disabled={isDisabledMaximumOrder}
              />
            </div>
            <ValidateMessageIfError fontSize={16} marginVertical={8} messages={errors} />
          </>
        )}
      </CSSTransition>
    </StyledOrderBread>
  );
};

const StyledOrderBread = styled.li<{ selected: boolean; isStopped: boolean }>`
  padding: 20px 16px;
  background: ${StyleConfig.COLOR.WHITE};
  margin-top: 10px;
  max-height: 200px;
  transition: max-height 0.5s;
  overflow: hidden;
  ${({ selected }) => {
    if (selected) {
      return `
        box-shadow: 0px 0px 1px 2px ${StyleConfig.COLOR.PRIMARY};
        max-height 400px;
      `;
    }
  }};
  ${({ isStopped }) => {
    if (isStopped) {
      return `
        filter: grayscale(100);
        background-color: ${StyleConfig.COLOR.BG_GRAY};
      `;
    }
  }};

  input[type='checkbox'] {
    border: 1px solid ${StyleConfig.COLOR.INPUT_BORDER} !important;
  }

  .content {
    display: flex;
    align-items: center;
    overflow: hidden;
    &:not(:first-child) {
      margin-top: 8px;
    }

    .content-thumbnail {
      width: 84px;
      height: 84px;
      overflow: hidden;
      position: relative;

      img {
        position: absolute;
        top: 50%;
        left: 50%;
        -webkit-transform: translate(-50%, -50%);
        -ms-transform: translate(-50%, -50%);
        transform: translate(-50%, -50%);
        width: 100%;
        height: auto;
        cursor: pointer;
      }
    }

    .content-text {
      margin: 0 8px 0 16px;
      cursor: pointer;
      h3 {
        font-size: 16px;
        line-height: ${20 / 16};
        color: ${StyleConfig.COLOR.TEXT_COLOR};
      }

      p {
        font-size: 12px;
      }

      .content-text-former {
        color: ${StyleConfig.COLOR.TEXT_GRAY};
        margin: 4px 0;
      }

      .content-text-count {
        color: ${StyleConfig.COLOR.TEXT_COLOR};
        span {
          margin-top: 4px;
          color: ${StyleConfig.COLOR.PRIMARY};
          font-weight: bold;
        }
      }
    }
  }

  .input-quantity {
    margin: 12px 0;
  }

  .forms {
    &-enter {
      opacity: 0.01;
    }
    &-enter-active {
      opacity: 0.01;
    }
    &-enter-done {
      opacity: 1;
      transition: opacity 500ms linear;
    }
    &-exit {
      opacity: 1;
    }
    &-exit-active {
      opacity: 0.01;
      transition: opacity 500ms linear;
    }
  }

  .form {
    margin-top: 18px;
    display: flex;

    ${media.greaterThan('medium')`
      justify-content: flex-end;
    `};

    .form-quantity {
      margin-right: 8px;
    }
    .form-maximumOrder {
      margin-left: 8px;
    }
  }
`;

const StyledToggleButton = styled.div<{ selected: boolean }>`
  margin-left: auto;
`;

export default NotificationCreateOrderBread;
